/**
 * Live read of her saved location, for every card and tool that needs one (#533).
 *
 * Deliberately a derived read off `usePreferences()` — no query of its own. The
 * app already had a lock-starvation outage from components each fetching their
 * own auth/prefs state (see CLAUDE.md, June 29), and this hook gets mounted by
 * TasksTab plus three planner tools. Everything here comes out of the single
 * `user_preferences` row the provider already loaded.
 *
 * `place` is resolved from the zip through zippopotam and cached by zip, so the
 * network call happens once per zip per device and every later open is instant.
 */

import { useEffect, useRef, useState } from "react";
import { usePreferences } from "@/hooks/useUserPreferences";
import {
  lookupZipLocation,
  readCachedZipLocation,
  resolveSavedZip,
  type CityState,
} from "@/lib/userLocation";

export interface SavedLocation {
  /** Her canonical zip, or null if she's never saved one. */
  zip: string | null;
  /** City + state resolved from that zip, or null while resolving / on failure. */
  place: CityState | null;
  /** True while prefs are loading or the zip lookup is in flight. */
  loading: boolean;
}

export function useSavedLocation(): SavedLocation {
  const { prefs, loading: prefsLoading } = usePreferences();
  const zip = prefsLoading ? null : resolveSavedZip(prefs);

  const [place, setPlace] = useState<CityState | null>(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!zip) {
      setPlace(null);
      setResolving(false);
      return;
    }
    const cached = readCachedZipLocation(zip);
    if (cached) {
      setPlace(cached);
      setResolving(false);
      return;
    }
    let cancelled = false;
    setPlace(null);
    setResolving(true);
    void lookupZipLocation(zip).then((resolved) => {
      if (cancelled) return;
      setPlace(resolved);
      setResolving(false);
    });
    return () => {
      cancelled = true;
    };
  }, [zip]);

  return { zip, place, loading: prefsLoading || resolving };
}

/**
 * Seeds a City/State pair from her saved location, once per sheet open, and
 * only while both fields are still empty.
 *
 * The emptiness check is the whole safety property: if she has already typed
 * anything, a slow zip lookup landing afterwards must not overwrite it. Same
 * reason `filledRef` exists — after one successful seed we never touch the
 * fields again for that open, so clearing a prefilled city to type her own
 * can't get undone underneath her.
 */
export function useCityStatePrefill(
  open: boolean,
  place: CityState | null,
  current: CityState,
  apply: (place: CityState) => void
): void {
  const currentRef = useRef(current);
  currentRef.current = current;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const filledRef = useRef(false);

  // Declared first so it runs before the seeding effect below on the same
  // commit — a close/reopen cycle must re-arm, an open must not un-arm.
  useEffect(() => {
    if (!open) filledRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open || filledRef.current || !place) return;
    const { city, stateCode } = currentRef.current;
    if (city.trim() !== "" || stateCode.trim() !== "") return;
    filledRef.current = true;
    applyRef.current(place);
  }, [open, place?.city, place?.stateCode]);
}

/**
 * Same idea for a single zip field (Summer Planner, Garden Planner, Kroger).
 * `enabled` lets a caller hold off until its own view is actually showing.
 */
export function useZipPrefill(
  enabled: boolean,
  savedZip: string | null,
  current: string,
  apply: (zip: string) => void
): void {
  const currentRef = useRef(current);
  currentRef.current = current;
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const filledRef = useRef(false);

  useEffect(() => {
    if (!enabled) filledRef.current = false;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || filledRef.current || !savedZip) return;
    if (currentRef.current.trim() !== "") return;
    filledRef.current = true;
    applyRef.current(savedZip);
  }, [enabled, savedZip]);
}
